/**
 * Deskryptory kolumn tabeli pozycji dokumentu — most między kolumnami pozycji
 * (`@/lib/documents/document-items`: wbudowane + pola dodatkowe `ext:<slug>`) a kontraktem układu
 * kolumn tabel osadzonych (`@saas/shared` `embedded-table-columns`). Używają go zarówno widok
 * odczytu pozycji na stronie dokumentu (`DocumentItemsSection`), jak i edytor układu kolumn
 * w kreatorze formatki (`DocumentItemsColumnsCanvas`).
 *
 * Kolumna `actions` (usuwanie wiersza) NIE jest kolumną układu — w edycji zawsze zostaje na końcu
 * tabeli, więc deskryptorów dla niej nie ma.
 *
 * Starszy zapis układu to `settings.columnConfig` (`{ id, visible, order }[]`); czyta go
 * `documentItemsLegacyReader` — wyłącznie wtedy, gdy `settings.tableColumns` brak (reguła wspólna
 * kontraktu), a pełny zapis z edytora czyści ten klucz (`DOCUMENT_ITEMS_LEGACY_KEYS`).
 */

import type {
  EmbeddedTableColumn,
  EmbeddedTableColumnDescriptor,
  EmbeddedTableColumnKind,
  EmbeddedTableLegacyReader,
} from '@saas/shared';

import {
  type BuiltInDocumentItemColumnId,
  COLUMN_HEADER_KEYS,
  type DocumentItemColumnId,
  EDITOR_COLUMN_IDS,
  extColumnId,
  extColumnSlug,
  isExtColumnId,
  SECTION_COLUMN_IDS,
} from '@/lib/documents/document-items';
import type { DocumentItemExtFieldDef } from '@/lib/documents/use-document-item-ext-fields';

import { columnKindOf } from './additional-table-descriptors';

/** Klucz starszego zapisu układu w `settings` — czyszczony przy pełnym zapisie edytora. */
export const DOCUMENT_ITEMS_LEGACY_KEYS: readonly string[] = ['columnConfig'];

/** Wbudowana kolumna układu — każda wbudowana oprócz `actions`. */
export type DocumentItemLayoutColumnId = Exclude<BuiltInDocumentItemColumnId, 'actions'>;

function isLayoutBuiltInColumnId(id: DocumentItemColumnId): id is DocumentItemLayoutColumnId {
  return id !== 'actions' && !isExtColumnId(id);
}

/** Wbudowane kolumny układu w naturalnej kolejności edytora (bez `actions`). */
export const DOCUMENT_ITEMS_BUILT_IN_COLUMN_IDS: readonly DocumentItemLayoutColumnId[] =
  EDITOR_COLUMN_IDS.filter(isLayoutBuiltInColumnId);

/** Rodzaj danych wbudowanych kolumn (pełne pokrycie — nowa kolumna nie skompiluje się bez rodzaju). */
const BUILT_IN_KIND: Record<DocumentItemLayoutColumnId, EmbeddedTableColumnKind> = {
  lineNumber: 'integer',
  product: 'text',
  name: 'text',
  productCode: 'text',
  unit: 'text',
  quantity: 'decimal',
  unitPriceNet: 'decimal',
  discountPercent: 'decimal',
  vatRateCode: 'text',
  pkwiu: 'text',
  cn: 'text',
  gtin: 'text',
  valueNet: 'decimal',
  valueVat: 'decimal',
  valueGross: 'decimal',
};

/**
 * Kolumny wymagane — nie da się ich ukryć: serwer odrzuca pozycję bez nazwy, ilości, ceny
 * jednostkowej i stawki VAT (decyzja biznesowa).
 */
const REQUIRED_COLUMN_IDS: ReadonlySet<DocumentItemLayoutColumnId> = new Set([
  'name',
  'quantity',
  'unitPriceNet',
  'vatRateCode',
]);

/** Kolumny zablokowane przez system — nie do ukrycia (numeracja porządkowa). */
const LOCKED_LAYOUT_COLUMN_IDS: ReadonlySet<DocumentItemLayoutColumnId> = new Set(['lineNumber']);

/**
 * Kolumny tylko-edycyjne: widok odczytu ich nie rysuje (`SECTION_COLUMN_IDS`), więc zapisany
 * układ je wymienia, ale na stronie dokumentu poza edycją są po cichu pomijane — edytor układu
 * oznacza je adnotacją „tylko w edycji”. Wyprowadzone z różnicy zestawów, nie z ręcznej listy:
 * nowa kolumna dodana kiedyś tylko do edytora dostaje oznaczenie sama.
 */
const EDIT_ONLY_COLUMN_IDS: ReadonlySet<DocumentItemLayoutColumnId> = new Set(
  DOCUMENT_ITEMS_BUILT_IN_COLUMN_IDS.filter(
    (id) => !SECTION_COLUMN_IDS.includes(id) && !LOCKED_LAYOUT_COLUMN_IDS.has(id),
  ),
);

/** Pole dodatkowe pozycji w tyle, ile potrzeba do zbudowania kolumny. */
export type DocumentItemExtColumnField = Pick<
  DocumentItemExtFieldDef,
  'slug' | 'name' | 'fieldType' | 'isRequired'
>;

/**
 * Deskryptory kolumn pozycji dokumentu: 15 wbudowanych (naturalna kolejność edytora, bez
 * `actions`), potem `ext:<slug>` żywych pól dodatkowych w kolejności pól. Etykiety wbudowanych
 * przez `t`, pól dodatkowych = ich nazwa; rodzaj pola dodatkowego wg tej samej reguły co tabele
 * dodatkowe; pole dodatkowe wymagane jest kolumną nie do ukrycia (jak w tabelach dodatkowych).
 *
 * Wyrównanie domyślne = reguła wg rodzaju (liczby do prawej) — dokładnie jak dziś w edycji i
 * odczycie; wyjątkiem jest `lineNumber` (liczba całkowita, ale dziś do lewej).
 */
export function documentItemDescriptors(
  t: (key: string) => string,
  extFields: readonly DocumentItemExtColumnField[],
): EmbeddedTableColumnDescriptor[] {
  const builtIns = DOCUMENT_ITEMS_BUILT_IN_COLUMN_IDS.map((id) => {
    const descriptor: EmbeddedTableColumnDescriptor = {
      id,
      label: t(COLUMN_HEADER_KEYS[id]),
      kind: BUILT_IN_KIND[id],
    };
    if (REQUIRED_COLUMN_IDS.has(id)) descriptor.required = true;
    if (LOCKED_LAYOUT_COLUMN_IDS.has(id)) descriptor.locked = true;
    if (id === 'lineNumber') descriptor.defaultAlign = 'left';
    if (EDIT_ONLY_COLUMN_IDS.has(id)) {
      descriptor.editorNote = t('tenant.documents.items.columnLayout.editOnlyNote');
    }
    return descriptor;
  });
  const ext = extFields.map((field): EmbeddedTableColumnDescriptor => {
    const descriptor: EmbeddedTableColumnDescriptor = {
      id: extColumnId(field.slug),
      label: field.name,
      kind: columnKindOf(field.fieldType),
    };
    if (field.isRequired) descriptor.required = true;
    return descriptor;
  });
  return [...builtIns, ...ext];
}

/**
 * Deskryptory kolumn, które umie wyrenderować widok odczytu (`SECTION_COLUMN_IDS` + pola
 * dodatkowe), w kolejności wejściowej, czyli edytora — bez zapisanego układu odczyt, edycja i
 * edytor układu w kreatorze mają tę samą kolejność. Kolumny
 * tylko-edycyjne (towar, kod, PKWiU, CN, GTIN) odpadają: zapisany układ może je wymieniać,
 * odczyt je po cichu pomija.
 */
export function documentItemsReadViewDescriptors(
  descriptors: readonly EmbeddedTableColumnDescriptor[],
): EmbeddedTableColumnDescriptor[] {
  return descriptors.filter(
    (descriptor) =>
      isExtColumnId(descriptor.id) ||
      SECTION_COLUMN_IDS.some((sectionId) => sectionId === descriptor.id),
  );
}

/**
 * Zawęża `id` kolumny układu do identyfikatora kolumny pozycji: wbudowana (z listy edytora) albo
 * `ext:<slug>`; nieznane → `undefined`.
 */
export function asDocumentItemColumnId(id: string): DocumentItemColumnId | undefined {
  if (isExtColumnId(id)) return extColumnId(extColumnSlug(id));
  return EDITOR_COLUMN_IDS.find((known) => known === id);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Czytnik starszego układu `settings.columnConfig` (`{ id, visible, order }[]`): sortuje po
 * `order` (stabilnie; brak liczby w `order` = pozycja we wpisie) i zwraca `{ id, visible }`.
 * Pomija `actions` (nie jest kolumną układu) oraz wpisy bez poprawnego `id`/`visible`.
 * Kolumny spoza zapisu dopisuje na końcu wspólna reguła kontraktu.
 */
export const documentItemsLegacyReader: EmbeddedTableLegacyReader = (settings) => {
  const raw = settings.columnConfig;
  if (!Array.isArray(raw)) return undefined;
  const entries: Array<{ id: string; visible: boolean; order: number }> = [];
  raw.forEach((item: unknown, index: number) => {
    if (!isPlainRecord(item)) return;
    const { id, visible, order } = item;
    if (typeof id !== 'string' || id === '' || id === 'actions') return;
    if (typeof visible !== 'boolean') return;
    entries.push({
      id,
      visible,
      order: typeof order === 'number' && Number.isFinite(order) ? order : index,
    });
  });
  entries.sort((a, b) => a.order - b.order);
  return entries.map((entry): EmbeddedTableColumn => ({ id: entry.id, visible: entry.visible }));
};
